import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en.json";
import ar from "./locales/ar.json";
import { preloadTranslations, translateDeep } from "./translationApi";
import { getServiceDetailsCopy, getServicesPageCopy, getSidebarLinks } from "../content/localeCopy";
import { cleanupThemeDomBeforeReactUpdate } from "../Utils/themeDomCleanup";

const STORAGE_KEY = "qtech_locale";
const bundles = { en, ar };
const fallbackMessages = bundles.en;
const STATIC_ARABIC_PRELOADS = [
  getServicesPageCopy(),
  getSidebarLinks(),
  getServiceDetailsCopy("property-management"),
  getServiceDetailsCopy("brokerage"),
  getServiceDetailsCopy("marketing"),
  getServiceDetailsCopy("maintenance"),
  getServiceDetailsCopy("development"),
  [
    "More about this service",
    "Service",
    "What We Provide",
    "Phase",
    "Details",
    "How we manage and protect property value",
    "Property upkeep and operational support",
    "Landscape quality and exterior presentation",
    "Your property presented with stronger market appeal",
    "Our marketing work combines visual presentation, market exposure and active follow-up to attract the right audience and improve conversion opportunities.",
    "Professional presentation and listing quality",
    "Campaign support and buyer-facing visibility",
    "Your Property’s Marketing Journey with Us",
    "How we connect the right parties with confidence",
    "Our brokerage service focuses on matching, negotiation, due diligence and transaction follow-up so deals move forward with more clarity, stronger protection and less delay.",
    "Buyer and seller coordination support",
    "Transaction readiness and guided follow-up",
    "Our Brokerage Workflow",
    "How we guide projects from concept to delivery",
    "Our development process combines planning, design coordination, execution supervision and commercial strategy to move projects forward with stronger quality control and long-term asset value.",
    "Planning, design, and licensing coordination",
    "Execution oversight and project value delivery",
    "Project Lifecycle Under Our Supervision",
    "Our Role as a Real Estate Developer",
    "How we protect performance and reduce long-term cost",
    "Our maintenance and operations approach combines planned inspections, rapid intervention and practical service coordination to keep facilities stable, safe and cost-efficient over time.",
    "Technical inspections and system upkeep",
    "Operational continuity and on-site support",
    "Scope of Our Technical Services",
    "Work Methodology: Preventative Maintenance",
  ],
];

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function pickLocalizedValue(record, key, locale, fallback = "") {
  if (!record) return fallback;

  const readValue = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value.trim() ? value : "";
    if (typeof value === "object") {
      const nestedLocalized =
        value?.[locale] ??
        value?.[locale?.toUpperCase?.()] ??
        value?.[locale === "ar" ? "arabic" : "english"] ??
        value?.[locale === "ar" ? "ar" : "en"];
      if (typeof nestedLocalized === "string" && nestedLocalized.trim()) return nestedLocalized;
    }
    return "";
  };

  const localizedCandidates =
    locale === "ar"
      ? [
          `${key}Ar`,
          `${key}_ar`,
          `${key}Arabic`,
          `${key}AR`,
        ]
      : [`${key}En`, `${key}_en`, `${key}English`, `${key}EN`];

  for (const candidate of localizedCandidates) {
    const localizedValue = readValue(record[candidate]);
    if (localizedValue) return localizedValue;
  }

  const translationObjectValue = readValue(record?.translations?.[key]);
  if (translationObjectValue) return translationObjectValue;

  const nestedKeyValue = readValue(record[key]);
  if (nestedKeyValue) {
    return nestedKeyValue;
  }

  const baseValue = record[key];
  if (baseValue !== undefined && baseValue !== null && String(baseValue).trim() !== "") {
    return baseValue;
  }
  return fallback;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "ar") return saved;
    } catch {
      /* ignore */
    }
    if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("ar")) {
      return "ar";
    }
    return "en";
  });

  const [messages, setMessages] = useState(() => bundles[locale] || bundles.en);

  const setLocale = useCallback((next) => {
    if (next !== "en" && next !== "ar") return;
    if (next === locale) return;
    if (typeof window !== "undefined") {
      cleanupThemeDomBeforeReactUpdate();
    }
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, [locale]);

  useEffect(() => {
    let cancelled = false;

    async function loadMessages() {
      if (locale === "en") {
        setMessages(bundles.en);
        return;
      }

      const hasArabicBundle = bundles.ar && Object.keys(bundles.ar).length > 0;
      if (hasArabicBundle) {
        setMessages(bundles.ar);
        return;
      }

      try {
        const translatedMessages = await translateDeep(bundles.en, "ar");
        if (!cancelled) setMessages(translatedMessages);
      } catch {
        if (!cancelled) setMessages(bundles.en);
      }
    }

    loadMessages();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const t = useCallback(
    (path) => {
      const v = getByPath(messages, path);
      if (v !== undefined && v !== null) return v;
      const fallback = getByPath(bundles.en, path);
      return fallback !== undefined && fallback !== null ? fallback : path;
    },
    [messages]
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", locale === "ar" ? "ar" : "en");
    root.setAttribute("dir", locale === "ar" ? "rtl" : "ltr");
    root.classList.toggle("qtech-rtl", locale === "ar");
    root.classList.toggle("qtech-ltr", locale !== "ar");
  }, [locale]);

  useEffect(() => {
    if (locale !== "ar") return undefined;

    let cancelled = false;

    const warmArabicCopy = async () => {
      try {
        await preloadTranslations(STATIC_ARABIC_PRELOADS, "ar");
      } catch {
        if (cancelled) return;
      }
    };

    warmArabicCopy();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
      messages,
      isRtl: locale === "ar",
      heroSlides: messages.home?.heroSlides || bundles.en.home.heroSlides,
      heroSubtitle: messages.home?.heroSubtitle || bundles.en.home.heroSubtitle,
      makeEnquiry: messages.home?.makeEnquiry || bundles.en.home.makeEnquiry,
      findNow: messages.home?.findNow || bundles.en.home.findNow,
      viewAll: messages.home?.viewAll || bundles.en.home.viewAll,
    }),
    [locale, setLocale, t, messages]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}

/** Labels for filter & sort UI (values stay English for URL / parsing). */
export function useFilterLabels() {
  const { messages } = useI18n();

  return useMemo(
    () => ({
      area: (value) =>
        messages.filters?.areas?.[value === "" ? "_all" : value] ??
        fallbackMessages.filters?.areas?.[value === "" ? "_all" : value] ??
        value,
      listing: (value) =>
        messages.filters?.listing?.[value] ?? fallbackMessages.filters?.listing?.[value] ?? value,
      type: (value) =>
        messages.filters?.type?.[value === "" ? "_all" : value] ??
        fallbackMessages.filters?.type?.[value === "" ? "_all" : value] ??
        value,
      price: (option) =>
        messages.filters?.price?.[option] ?? fallbackMessages.filters?.price?.[option] ?? option,
      sort: (sortValue) =>
        messages.property?.sort?.[sortValue] ??
        fallbackMessages.property?.sort?.[sortValue] ??
        sortValue,
    }),
    [messages]
  );
}
